import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Typography,
  useTheme
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import CountryStateFields from 'src/common/formFields/CountryStateFields';
import InputField from 'src/common/formFields/InputField';
import SelectField from 'src/common/formFields/SelectField';
import Loader from 'src/common/Loader';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { addSiteManager, updateSiteManager } from 'src/redux/siteManagerSlice';
import { getAllSites } from 'src/redux/siteSlice';
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS } from 'src/utils/timezoneUtils';
import {
  getSiteManagerValidationSchema,
  siteManagerFormInitialValues,
  siteManagerFormModel
} from './siteManagerFormModel';

const getInitialSiteIds = (values) => {
  if (Array.isArray(values.sites) && values.sites.length > 0) {
    return values.sites;
  }
  if (values.site !== null && values.site !== undefined) {
    return [values.site];
  }
  return [];
};

const NewSiteManager = ({ open, handleClose, initialValues }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { allSites } = useSelector((state) => state.sites);

  const [loading, setLoading] = useState(false);
  const requiredHintText = '';
  const formInitValues = initialValues
    ? {
      ...initialValues,
      sites: getInitialSiteIds(initialValues).map(Number).filter(Number.isFinite),
      timezone: initialValues.timezone || DEFAULT_TIMEZONE
    }
    : siteManagerFormInitialValues;
  const isEditMode = Boolean(initialValues);

  const {
    formId,
    formField: {
      sites,
      empId,
      firstName,
      lastName,
      username,
      password,
      confirmPassword,
      email,
      mobileNum,
      addressLine,
      city,
      state,
      pinCode,
      country,
      timezone
    }
  } = siteManagerFormModel;

  const siteNameById = useMemo(
    () => new Map(allSites.map((site) => [String(site.id), site.name])),
    [allSites]
  );

  const renderSelectedSites = (selected = []) => (Array.isArray(selected) ? selected : [selected])
    .map((siteId) => siteNameById.get(String(siteId)) || siteId)
    .join(', ');

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
    }
  }, [open, dispatch]);

  const onSubmit = async (values, actions) => {
    setLoading(true);
    try {
      const siteIds = (values.sites || []).map(Number);
      const payload = {
        ...values,
        sites: siteIds,
        site: siteIds[0]
      };

      if (initialValues) {
        await dispatch(updateSiteManager(payload)).unwrap();
      } else {
        await dispatch(addSiteManager(payload)).unwrap();
      }

      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Site Supervisor updated successfully'
            : 'New Site Supervisor created successfully'
        })
      );

      handleClose();
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    }

    setLoading(false);
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'UPDATE SITE SUPERVISOR' : 'ADD NEW SITE SUPERVISOR'}
        onCancel={handleClose}
      />

      <Divider />

      <Formik
        initialValues={formInitValues}
        enableReinitialize
        validationSchema={getSiteManagerValidationSchema(initialValues)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting }) => (
          <>
            {loading ? (
              <Loader />
            ) : (
              <Form
                id={formId}
                className="csp-form csp-form--80vh"
              >
                <DialogContent
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 3
                  }}
                >
                  {!isEditMode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      * Required fields
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={sites.name}
                        label={`${sites.label} *`}
                        data={allSites}
                        multiple
                        renderValue={renderSelectedSites}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={empId.name}
                        label={`${empId.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={firstName.name}
                        label={`${firstName.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={lastName.name}
                        label={`${lastName.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={username.name}
                        label={`${username.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={email.name}
                        label={`${email.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    {!initialValues && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={password.name}
                            label={`${password.label} *`}
                            type="password"
                            required
                            requiredHintText={requiredHintText}
                            fullWidth
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <InputField
                            name={confirmPassword.name}
                            label={`${confirmPassword.label} *`}
                            type="password"
                            required
                            requiredHintText={requiredHintText}
                            fullWidth
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={mobileNum.name}
                        label={`${mobileNum.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={addressLine.name}
                        label={`${addressLine.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={city.name}
                        label={`${city.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InputField
                        name={pinCode.name}
                        label={`${pinCode.label} *`}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>

                    <CountryStateFields
                      countryField={{ ...country, label: `${country.label} *` }}
                      stateField={{ ...state, label: `${state.label} *` }}
                      requiredHintText={requiredHintText}
                    />

                    <Grid item xs={12} sm={6}>
                      <SelectField
                        name={timezone.name}
                        label={timezone.label}
                        data={TIMEZONE_OPTIONS}
                        required
                        requiredHintText={requiredHintText}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>

                <Divider />

                <DialogActions
                  sx={{
                    p: 2.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: '#F9FAFB'
                  }}
                >
                  <Button
                    onClick={handleClose}
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3
                    }}
                  >
                    {initialValues ? 'Update Supervisor' : 'Create Supervisor'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </>
        )}
      </Formik>
    </FMSModal>
  );
};

NewSiteManager.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewSiteManager;
